import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, ExclamationCircleOutlined, FileSearchOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssistantExtraction, AssistantExtractionLine } from "@wms/domain";
import { Alert, App, Button, Card, Col, Descriptions, Empty, Grid, Image, Input, InputNumber, Row, Segmented, Select, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { assistant, invoiceFile, requestId } from "../api/assistant";
import { describeError } from "../api/client";
import { RefSelect } from "./RefSelect";
import { taxIdLabel } from "./kzRequisites";

const STATUS: Record<AssistantExtractionLine["status"], { label: string; color: string; icon: React.ReactNode }> = {
  recognized: { label: "Распознано", color: "blue", icon: <FileSearchOutlined /> },
  needs_review: { label: "Нужна проверка", color: "orange", icon: <ExclamationCircleOutlined /> },
  confirmed: { label: "Подтверждено", color: "green", icon: <CheckCircleOutlined /> },
  skipped: { label: "Пропущено", color: "default", icon: <MinusCircleOutlined /> },
};

function lineStatus(line: AssistantExtractionLine) {
  if (line.status === "confirmed" && line.state === "edited") return { label: "Исправлено вручную", color: "green", icon: <EditOutlined /> };
  return STATUS[line.status] ?? STATUS.needs_review;
}

/** The original next to the data (13.3): an image or the PDF, fetched with the owner's rights. */
function Original({ extraction }: { extraction: AssistantExtraction }) {
  const file = useQuery({ queryKey: ["assistant-source", extraction.source_id], queryFn: () => invoiceFile(extraction.source_url), staleTime: Infinity });
  useEffect(() => {
    const url = file.data?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.data?.url]);
  if (file.isLoading) return <Spin />;
  if (file.isError) return <Alert type="warning" showIcon message="Оригинал не открылся" description={describeError(file.error)} />;
  if (!file.data) return null;
  return file.data.type === "application/pdf" ? (
    <iframe title="Оригинал накладной" src={file.data.url} style={{ width: "100%", height: 620, border: 0, borderRadius: 8 }} />
  ) : (
    // a click opens it full size with zoom: the small print of an invoice is checked against the data
    <Image src={file.data.url} alt="Оригинал накладной" style={{ width: "100%", borderRadius: 8, border: "1px solid var(--wms-border)" }} />
  );
}

interface Props {
  extractionId: string;
  onCreated: () => void;
  onClose: () => void;
}

/**
 * «Проверка накладной» (ТЗ 13.3): what was read, what the server matched, what still needs a person. Every correction
 * is a new revision on the server; «Создать поступление / заказ» is open only when nothing blocks it, and it creates
 * a plan document — never a receipt of goods.
 */
export function AssistantReview({ extractionId, onCreated, onClose }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const narrow = screens.lg === false;
  const [pane, setPane] = useState<"data" | "document">("data");
  const key = ["assistant-extraction", extractionId];
  const query = useQuery({ queryKey: key, queryFn: () => assistant.extraction(extractionId) });
  const data = query.data;
  const [createKey] = useState(requestId);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => assistant.patchExtraction(extractionId, { revision: data?.revision, ...body }),
    onSuccess: (next) => queryClient.setQueryData(key, next),
    onError: (e) => {
      message.error(describeError(e), 6);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const create = useMutation({
    mutationFn: () => assistant.create(extractionId, data!.revision, createKey),
    onSuccess: (result) => {
      queryClient.setQueryData(key, result.extraction);
      message.success(result.action?.result?.text ?? "Документ создан", 8);
      onCreated();
    },
    onError: (e) => {
      message.error(describeError(e), 8);
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const counterpart = data?.direction === "shipment" ? data.recipient : data?.supplier;
  const partnerOptions = useMemo(
    // KZ 30.10.2026 (ТЗ реквизитов §11.4): «БИН» of a ТОО, «ИИН» of an ИП — the label of the card, never a guess
    () => (counterpart?.candidates ?? []).map((p) => ({ value: p.id, label: `${p.name}${p.bin ? ` · ${p.tax_id_label ?? taxIdLabel(p.kind)} ${p.bin}` : ""}` })),
    [counterpart],
  );
  if (query.isLoading) return <Spin />;
  if (query.isError || !data) return <Alert type="error" showIcon message="Сверка не открылась" description={describeError(query.error)} />;
  const busy = patch.isPending || create.isPending;
  const created = !!data.document;
  const unresolved = data.lines.filter((l) => l.status === "needs_review").length;

  const lineChange = (index: number, change: Record<string, unknown>) => patch.mutate({ lines: [{ index, ...change }] });

  const lines = (
    <Space direction="vertical" style={{ width: "100%" }} size={10}>
      {data.lines.map((line) => {
        const st = lineStatus(line);
        const chosen = line.candidates.find((c) => c.id === line.storage_object_id);
        const packages = (chosen?.packages ?? []).filter((p) => !p.base);
        const src = line.source;
        return (
          <Card key={line.index} size="small" className="wms-assistant-line" styles={{ body: { padding: 12 } }} data-testid={`review-line-${line.index}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <Typography.Text strong>
                Строка {line.index}
                {line.page ? <Typography.Text type="secondary"> · стр. {line.page}</Typography.Text> : null}
              </Typography.Text>
              <Tag color={st.color} icon={st.icon}>
                {st.label}
              </Tag>
            </div>
            <Typography.Paragraph type="secondary" style={{ margin: "4px 0 8px", fontSize: 13 }}>
              В документе: «{src.name}»{src.article ? `, арт. ${src.article}` : ""}
              {src.code ? `, код ${src.code}` : ""}
              {src.quantity ? `, ${src.quantity} ${src.unit ?? ""}` : ""}
              {src.packages_count ? `, ${src.packages_count} ${src.package ?? "уп."}${src.per_package ? ` по ${src.per_package}` : ""}` : ""}
              {src.batch ? `, партия ${src.batch}` : ""}
              {src.expiry ? `, годен до ${src.expiry}` : ""}
            </Typography.Paragraph>
            {line.status !== "skipped" ? (
              <Row gutter={[8, 8]}>
                <Col xs={24} md={12}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder={line.candidates.length ? "Выберите товар" : "Товар не найден — найдите в справочнике"}
                    value={line.storage_object_id ?? undefined}
                    disabled={busy || created}
                    onChange={(value) => lineChange(line.index, { storage_object_id: value })}
                    options={line.candidates.map((c) => ({
                      value: c.id,
                      label: `${c.code} · ${c.name}${c.packages.filter((p) => !p.base).length ? ` (${c.packages.filter((p) => !p.base).map((p) => `${p.name} = ${p.ratio} ${c.unit}`).join(", ")})` : ""}`,
                    }))}
                    aria-label={`Товар строки ${line.index}`}
                  />
                  {!created ? (
                    <div style={{ marginTop: 6 }}>
                      <RefSelect entity="storage_objects" placeholder="Другой товар из справочника" value={undefined} onChange={(value) => value && lineChange(line.index, { storage_object_id: value })} />
                    </div>
                  ) : null}
                </Col>
                <Col xs={24} md={12}>
                  <Space.Compact style={{ width: "100%" }}>
                    <Select
                      style={{ minWidth: 150 }}
                      allowClear
                      placeholder="Упаковка"
                      disabled={busy || created || !packages.length}
                      value={line.package_id ?? undefined}
                      onChange={(value) => lineChange(line.index, value ? { package_id: value } : { package_id: null })}
                      options={packages.map((p) => ({ value: p.id, label: `${p.name} = ${p.ratio} ${chosen?.unit ?? ""}` }))}
                    />
                    {line.package_id ? (
                      <InputNumber
                        min={1}
                        step={1}
                        style={{ width: "100%" }}
                        placeholder="Упаковок"
                        disabled={busy || created}
                        defaultValue={line.packages_count ? Number(line.packages_count) : undefined}
                        key={`pc-${line.package_id}-${data.revision}`}
                        onBlur={(e) => e.target.value && e.target.value !== line.packages_count && lineChange(line.index, { packages_count: e.target.value })}
                        aria-label={`Упаковок в строке ${line.index}`}
                      />
                    ) : (
                      <Input
                        style={{ width: "100%" }}
                        placeholder={`Количество${chosen ? `, ${chosen.unit}` : ""}`}
                        disabled={busy || created || !line.storage_object_id}
                        defaultValue={line.quantity ?? line.quantity_base ?? ""}
                        key={`q-${data.revision}`}
                        onBlur={(e) => e.target.value !== (line.quantity ?? line.quantity_base ?? "") && lineChange(line.index, { quantity: e.target.value })}
                        aria-label={`Количество в строке ${line.index}`}
                      />
                    )}
                  </Space.Compact>
                  {line.quantity_text ? <Typography.Text style={{ display: "block", marginTop: 6 }}>Итого: {line.quantity_text}</Typography.Text> : null}
                </Col>
              </Row>
            ) : null}
            {line.issues.length ? (
              <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "var(--wms-warning, #b45309)" }}>
                {line.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            ) : null}
            {!created ? (
              <Space style={{ marginTop: 8 }} wrap>
                {line.status === "needs_review" && line.storage_object_id ? (
                  <Button size="small" icon={<CheckCircleOutlined />} disabled={busy} onClick={() => lineChange(line.index, { confirm: true })}>
                    Подтвердить строку
                  </Button>
                ) : null}
                {line.status !== "skipped" ? (
                  <Button size="small" icon={<CloseCircleOutlined />} disabled={busy} onClick={() => lineChange(line.index, { skip: true })}>
                    Пропустить строку
                  </Button>
                ) : null}
              </Space>
            ) : null}
          </Card>
        );
      })}
    </Space>
  );

  const summary = (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Descriptions size="small" column={1} colon>
        <Descriptions.Item label="Документ">
          {data.doc_type_label}
          {data.title ? ` — «${data.title}»` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Что создаём">
          <Segmented
            size="small"
            disabled={busy || created}
            value={data.direction === "shipment" ? "shipment" : data.direction === "receipt" ? "receipt" : undefined}
            onChange={(value) => patch.mutate({ direction: value })}
            options={[
              { value: "receipt", label: "Поступление к нам" },
              { value: "shipment", label: "Заказ на отгрузку" },
            ]}
          />
        </Descriptions.Item>
        <Descriptions.Item label={data.direction === "shipment" ? "Клиент" : "Поставщик"}>
          <Space direction="vertical" style={{ width: "100%" }} size={4}>
            <Typography.Text type="secondary" data-testid="review-party-printed">
              В документе: {counterpart?.name ?? "не прочитан"}
              {counterpart?.tax_id?.value ? `, ${counterpart.tax_id.label ?? "Идентификатор в оригинале"} ${counterpart.tax_id.value}` : counterpart?.bin ? `, БИН/ИИН ${counterpart.bin}` : ""}
              {counterpart?.tax_id?.country ? ` (${counterpart.tax_id.country})` : ""}
            </Typography.Text>
            {counterpart?.hint ? <Alert type={counterpart.foreign ? "info" : "warning"} showIcon message={counterpart.hint} data-testid="review-party-hint" /> : null}
            {partnerOptions.length ? (
              <Select
                style={{ width: "100%" }}
                aria-label="Контрагент"
                placeholder="Выберите контрагента"
                value={data.partner_id ?? undefined}
                disabled={busy || created}
                onChange={(value) => patch.mutate({ partner_id: value })}
                options={partnerOptions}
              />
            ) : null}
            {!created ? <RefSelect entity="partners" placeholder="Найти контрагента в справочнике" value={undefined} onChange={(value) => value && patch.mutate({ partner_id: value })} /> : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Номер и дата">
          <Space.Compact style={{ width: "100%" }}>
            <Input defaultValue={data.number.value ?? ""} key={`n-${data.revision}`} disabled={busy || created} placeholder="Номер" onBlur={(e) => e.target.value !== (data.number.value ?? "") && patch.mutate({ number: e.target.value })} />
            <Input
              defaultValue={data.date.value ? data.date.value.split("-").reverse().join(".") : data.date.raw ?? ""}
              key={`d-${data.revision}`}
              disabled={busy || created}
              placeholder="ДД.ММ.ГГГГ"
              onBlur={(e) => {
                const shown = data.date.value ? data.date.value.split("-").reverse().join(".") : data.date.raw ?? "";
                if (e.target.value !== shown) patch.mutate({ date: e.target.value });
              }}
            />
          </Space.Compact>
          {data.date.problem ? <Typography.Text type="warning">{data.date.problem}</Typography.Text> : null}
        </Descriptions.Item>
        <Descriptions.Item label="Строк">
          {data.lines.length}
          {unresolved ? `, требуют проверки: ${unresolved}` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="Итого">{data.totals.length ? data.totals.map((t) => `${t.quantity} ${t.unit}`).join(" · ") : "—"}</Descriptions.Item>
      </Descriptions>
      {data.problems.length ? <Alert style={{ marginTop: 8 }} type="warning" showIcon message="Замечания распознавания" description={data.problems.join("; ")} /> : null}
    </Card>
  );

  const footer = created ? (
    <Alert
      type="success"
      showIcon
      message="Документ создан"
      description={
        <>
          Это плановый документ: товар ещё не принят. <Link to={data.document!.path}>Открыть документ</Link>
        </>
      }
    />
  ) : (
    <Space direction="vertical" style={{ width: "100%" }}>
      {data.blocking.length ? (
        <Alert
          type="info"
          showIcon
          message="Чтобы создать документ"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {data.blocking.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      <Button type="primary" size="large" block disabled={!!data.blocking.length || busy} loading={create.isPending} onClick={() => create.mutate()} data-testid="review-create">
        {data.button}
      </Button>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Будет создан документ с плановым количеством; фактическая приёмка или отгрузка — штатно на ТСД со сканами.
      </Typography.Text>
    </Space>
  );

  return (
    <div data-testid="assistant-review">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Проверка накладной
        </Typography.Title>
        <Space>
          {narrow ? (
            <Segmented
              size="small"
              value={pane}
              onChange={(v) => setPane(v as "data" | "document")}
              options={[
                { value: "data", label: "Данные" },
                { value: "document", label: "Документ" },
              ]}
            />
          ) : null}
          <Button size="small" onClick={onClose}>
            Закрыть
          </Button>
        </Space>
      </div>
      {data.status === "failed" ? (
        <Empty description={data.error ?? "Распознавание не удалось"} />
      ) : narrow ? (
        pane === "document" ? (
          <Original extraction={data} />
        ) : (
          <>
            {summary}
            {lines}
            <div style={{ marginTop: 12 }}>{footer}</div>
          </>
        )
      ) : (
        <Row gutter={16}>
          <Col span={10}>
            <div style={{ position: "sticky", top: 12 }}>
              <Original extraction={data} />
            </div>
          </Col>
          <Col span={14}>
            {summary}
            {lines}
            <div style={{ marginTop: 12 }}>{footer}</div>
          </Col>
        </Row>
      )}
    </div>
  );
}
